import { useCallback, useMemo } from "react";
import {
  Avatar,
  Box,
  Button,
  Divider,
  FormControl,
  FormLabel,
  HStack,
  Switch,
  Text,
  Flex,
  Input,
  VStack,
} from "@chakra-ui/react";
import { creativeBlockHasPartnerPost } from "@common/utils/instagramPartnershipCreative";
import { Select } from "chakra-react-select";
import { FaTrash } from "react-icons/fa";
import type { CreativeBlock } from "../guidedFlow/types";
import type { FacebookPage, InstagramAccount } from "../types";
import { resolveAdvertiserIdentityForPartnership } from "../utils/partnershipPartnerOptionsFetch";
import { useAdvertiserInstagramPageSync } from "../hooks/useAdvertiserInstagramPageSync";
import WhiteBgInput from "../WhiteBgInput";
import { PRIMARY_TEXT_MAX } from "../forms/campaignFlowForm";
import MediaUploadArea from "./MediaUploadArea";
import InstagramPartnerPanel from "./InstagramPartnerPanel";
import {
  creativeBlockHasSquareAdSlot,
  creativeBlockHasVerticalAdSlot,
} from "../utils/creativeBlockAdPreviewSlots";
import {
  CREATIVE_IDENTITY_SELECT_CONTROL_Z,
  CREATIVE_IDENTITY_SELECT_MENU_Z,
  withCreativeSelectStackOrder,
} from "../utils/creativeBlockSelectStyles";

export type CreativeBlockEditorProps = {
  creative: CreativeBlock;
  index: number;
  totalCreatives: number;
  pageOptions: { label: string; value: FacebookPage }[];
  instagramOptions: { label: string; value: InstagramAccount }[];
  createUploadUrlForSlot: (
    slotKey: string
  ) => (opts: {
    file: File;
    callback: (r: { signedUrl: string }) => void | Promise<string | void>;
  }) => Promise<{ signedUrl: string; fileKey: string }>;
  uploadRefs: React.MutableRefObject<
    Record<string, { fileKey?: string; cdn_url?: string }>
  >;
  onUpdate: (index: number, patch: Partial<CreativeBlock>) => void;
  onRemove: (index: number) => void;
  onAddMore: () => void;
  /**
   * When the user picks a Facebook page, fetch Instagram actors for that page.
   * Return rows so the editor can merge: by default only canonicalizes an already
   * selected id; see {@link applyDefaultInstagramOnFacebookPageChange}.
   */
  onFacebookPageChange?: (
    facebookPageId: string
  ) => void | Promise<InstagramAccount[] | void>;
  /** Page-scoped IG fetch when a partner Facebook page is selected. */
  onPartnerFacebookPageChange?: (
    facebookPageId: string
  ) => void | Promise<InstagramAccount[] | void>;
  /**
   * When true (guided create), after a successful fetch the first Instagram account
   * is selected if the row had none. Edit flows still auto-select the page-linked
   * Instagram when Meta exposes one; otherwise Instagram stays empty until picked.
   */
  applyDefaultInstagramOnFacebookPageChange?: boolean;
  editingName: { index: number; value: string } | null;
  onEditingNameChange: (v: { index: number; value: string } | null) => void;
  mediaValidationMessage?: string | null;
  allowCreativeCountChanges?: boolean;
  isDisabled?: boolean;
  /** When true, the Facebook Page select is disabled and shows a loading state. */
  isFacebookPagesLoading?: boolean;
  /** When true, the Instagram select is disabled and shows a loading state (e.g. after page change). */
  isInstagramAccountsLoading?: boolean;
  /** When true, the Instagram select cannot be cleared (clear control hidden). */
  instagramAccountClearDisabled?: boolean;
  /** Ad account for partnership partner-options API (guided flow). */
  adAccountId?: string;
  partnerPages?: FacebookPage[];
  partnerInstagramAccounts?: InstagramAccount[];
  partnershipOptionsLoading?: boolean;
  partnershipPermissionsLoaded?: boolean;
  partnershipOptionsError?: string | null;
  metaBusinessId?: string;
  advertiserValidationMessages?: string[];
  onRefreshPartnershipOptions?: () => Promise<void>;
  onPartnershipPermissionLookupLoadingChange?: (loading: boolean) => void;
  /** When false, partner post toggle and panel are hidden. */
  partnerPostsEnabled?: boolean;
  selectContainerStyles: Record<
    string,
    (p: Record<string, unknown>) => Record<string, unknown>
  >;
};

export default function CreativeBlockEditor({
  creative,
  index,
  totalCreatives,
  pageOptions,
  instagramOptions,
  createUploadUrlForSlot,
  uploadRefs,
  onUpdate,
  onRemove,
  onAddMore,
  onFacebookPageChange,
  onPartnerFacebookPageChange,
  applyDefaultInstagramOnFacebookPageChange = false,
  editingName,
  onEditingNameChange,
  mediaValidationMessage,
  allowCreativeCountChanges = true,
  isDisabled = false,
  isFacebookPagesLoading = false,
  isInstagramAccountsLoading = false,
  instagramAccountClearDisabled = false,
  adAccountId,
  partnerPages,
  partnerInstagramAccounts,
  partnershipOptionsLoading = false,
  partnershipPermissionsLoaded = false,
  partnershipOptionsError = null,
  advertiserValidationMessages = [],
  onRefreshPartnershipOptions,
  onPartnershipPermissionLookupLoadingChange,
  partnerPostsEnabled = false,
  selectContainerStyles,
}: CreativeBlockEditorProps) {
  const catalogPages = useMemo(
    () => pageOptions.map((option) => option.value),
    [pageOptions]
  );
  const catalogAccounts = useMemo(
    () => instagramOptions.map((option) => option.value),
    [instagramOptions]
  );
  const applyCreativePatch = useCallback(
    (patch: Partial<CreativeBlock>) => onUpdate(index, patch),
    [index, onUpdate]
  );
  const {
    mergedInstagramOptions,
    handleFacebookPageChange,
    handleInstagramChange,
  } = useAdvertiserInstagramPageSync({
    creative,
    catalogPages,
    catalogAccounts,
    onFacebookPageChange,
    applyDefaultOnFacebookPageChange:
      applyDefaultInstagramOnFacebookPageChange,
    instagramClearDisabled: instagramAccountClearDisabled,
    onPatch: applyCreativePatch,
  });

  const partnerPostEnabled =
    partnerPostsEnabled && creativeBlockHasPartnerPost(creative);
  const advertiserInstagramUserId = resolveAdvertiserIdentityForPartnership(
    creative,
    instagramOptions.map((option) => option.value)
  ).advertiserInstagramUserId;
  const identityFacebookSelectStyles = withCreativeSelectStackOrder(
    selectContainerStyles,
    CREATIVE_IDENTITY_SELECT_CONTROL_Z + 1,
    CREATIVE_IDENTITY_SELECT_MENU_Z
  );
  const identityInstagramSelectStyles = withCreativeSelectStackOrder(
    selectContainerStyles,
    CREATIVE_IDENTITY_SELECT_CONTROL_Z,
    CREATIVE_IDENTITY_SELECT_MENU_Z
  );
  const slotKeySquare = `${index}-square`;
  const slotKeyVertical = `${index}-vertical`;
  const isEditingName = editingName?.index === index;
  const hasSquare = creativeBlockHasSquareAdSlot(creative);
  const hasVertical = creativeBlockHasVerticalAdSlot(creative);

  return (
    <Box
      backgroundColor="white"
      rounded="lg"
      p={4}
      border="1px solid"
      borderColor="gray.200"
      pb={8}
    >
      <HStack
        justify="space-between"
        align="center"
        w="full"
        mb={2}
        flexWrap="wrap"
        gap={2}
      >
        {isEditingName ? (
          <HStack align="center" spacing={2} flex={1} minW="200px">
            <Input
              size="sm"
              value={editingName.value}
              isDisabled={isDisabled}
              onChange={(e) =>
                onEditingNameChange(
                  editingName ? { ...editingName, value: e.target.value } : null
                )
              }
              onKeyDown={(e) => {
                if (isDisabled) {
                  return;
                }
                if (e.key === "Enter") {
                  onUpdate(index, {
                    name: editingName.value.trim() || undefined,
                  });
                  onEditingNameChange(null);
                }
                if (e.key === "Escape") {
                  onEditingNameChange(null);
                }
              }}
              autoFocus
              placeholder={`Ad ${index + 1}`}
            />
            <Button
              size="sm"
              bgColor="gray.800"
              color="gray.50"
              px={4}
              isDisabled={isDisabled}
              _hover={{ bgColor: "blue.800" }}
              onClick={() => {
                onUpdate(index, {
                  name: editingName.value.trim() || undefined,
                });
                onEditingNameChange(null);
              }}
            >
              Save
            </Button>
            <Button
              px={4}
              size="sm"
              variant="ghost"
              isDisabled={isDisabled}
              onClick={() => onEditingNameChange(null)}
            >
              Cancel
            </Button>
          </HStack>
        ) : (
          <>
            <HStack align="center" spacing={2} flexWrap="wrap" px={2}>
              <Text fontSize="sm" fontWeight="semibold">
                {creative.name || `Ad ${index + 1}`}
              </Text>
              <Button
                size="xs"
                variant="ghost"
                colorScheme="blue"
                isDisabled={isDisabled}
                onClick={() =>
                  onEditingNameChange({
                    index,
                    value: creative.name || `Ad ${index + 1}`,
                  })
                }
              >
                Rename
              </Button>
            </HStack>
            <Text
              fontSize="xs"
              textAlign={{
                base: "center",
                "2xl": mediaValidationMessage ? "right" : "center",
              }}
              width={{ base: "100%", "2xl": "auto" }}
              color={
                mediaValidationMessage
                  ? "red.500"
                  : hasSquare && hasVertical
                  ? "green.600"
                  : hasSquare || hasVertical
                  ? "green.600"
                  : "gray.500"
              }
              data-testid={`meta-ads-creative-formats-status-${index}`}
            >
              {mediaValidationMessage ? (
                <>
                  {mediaValidationMessage
                    .split("\n")
                    .filter(Boolean)
                    .map((sentence, idx, arr) => (
                      <span key={idx}>
                        {sentence.trim()}
                        {idx !== arr.length - 1 && <br />}
                      </span>
                    ))}
                </>
              ) : hasSquare && hasVertical ? (
                "Both formats added"
              ) : !hasSquare && !hasVertical ? (
                "Add at least one format (square 1:1 or vertical 9:16)"
              ) : !hasSquare ? (
                "Square optional (1:1)"
              ) : (
                "Vertical optional (9:16)"
              )}
            </Text>
          </>
        )}
      </HStack>

      <HStack align="flex-start" spacing={4} flexWrap="wrap" w="100%">
        <Box flex="1" minW={0}>
          <Box
            w="100%"
            aspectRatio={"1"}
            border="1px dashed"
            borderColor="rgba(0, 0, 0, 0.3)"
            rounded="lg"
            bg="gray.50"
            _hover={{ borderColor: "gray.400" }}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <MediaUploadArea
              variant="modern"
              ratio="1/1"
              name="Square"
              fileInputTestId={`meta-ads-creative-upload-${index}-square`}
              noPadding={true}
              mediaType={
                creative.squareMediaType === "video" ? "video" : "photo"
              }
              url={creative.squareMediaUrl}
              createUploadUrl={createUploadUrlForSlot(slotKeySquare)}
              onChange={(result) => {
                if (!result.mime_type?.trim()) {
                  onUpdate(index, {
                    squareMediaUrl: undefined,
                    squareMediaFileKey: undefined,
                    squareMediaType: undefined,
                  });
                  return;
                }
                onUpdate(index, {
                  squareMediaUrl:
                    result.cdn_url ??
                    uploadRefs.current[slotKeySquare]?.cdn_url,
                  squareMediaFileKey:
                    result.file_key ??
                    uploadRefs.current[slotKeySquare]?.fileKey,
                  squareMediaType: result.mime_type.startsWith("video/")
                    ? "video"
                    : "image",
                });
              }}
              isDisabled={isDisabled}
              p={4}
            />
          </Box>
        </Box>
        <Box flex="1" minW={0}>
          <Box
            w="100%"
            aspectRatio={"1"}
            border="1px dashed"
            borderColor="rgba(0, 0, 0, 0.3)"
            rounded="lg"
            bg="gray.50"
            _hover={{ borderColor: "gray.400" }}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <MediaUploadArea
              variant="modern"
              ratio="9/16"
              name="Vertical"
              fileInputTestId={`meta-ads-creative-upload-${index}-vertical`}
              noPadding={true}
              mediaType={
                creative.verticalMediaType === "video" ? "video" : "photo"
              }
              url={creative.verticalMediaUrl}
              createUploadUrl={createUploadUrlForSlot(slotKeyVertical)}
              onChange={(result) => {
                if (!result.mime_type?.trim()) {
                  onUpdate(index, {
                    verticalMediaUrl: undefined,
                    verticalMediaFileKey: undefined,
                    verticalMediaType: undefined,
                  });
                  return;
                }
                onUpdate(index, {
                  verticalMediaUrl:
                    result.cdn_url ??
                    uploadRefs.current[slotKeyVertical]?.cdn_url,
                  verticalMediaFileKey:
                    result.file_key ??
                    uploadRefs.current[slotKeyVertical]?.fileKey,
                  verticalMediaType: result.mime_type.startsWith("video/")
                    ? "video"
                    : "image",
                });
              }}
              isDisabled={isDisabled}
              p={4}
            />
          </Box>
        </Box>
      </HStack>
      <FormControl mt={4}>
        <Flex justify="space-between" align="center" mb={1}>
          <FormLabel fontSize="sm" fontWeight="semibold" mb={0}>
            Primary text
          </FormLabel>
          <Text fontSize="xs" color="gray.500">
            {creative.primaryText.length}/{PRIMARY_TEXT_MAX} Characters
          </Text>
        </Flex>
        <WhiteBgInput
          as="textarea"
          resize="vertical"
          maxLength={PRIMARY_TEXT_MAX}
          isDisabled={isDisabled}
          value={creative.primaryText}
          onChange={(e) =>
            onUpdate(index, {
              primaryText: e.target.value.slice(0, PRIMARY_TEXT_MAX),
            })
          }
          placeholder="Primary text here"
          style={{ paddingTop: "5px", display: "flex", minHeight: "54px" }}
        />
      </FormControl>

      <Box mt={4} pt={4}>
        <Divider mb={4} borderColor="gray.100" />
        <VStack align="stretch" spacing={3}>
          <HStack justify="space-between" align="center" w="full">
            <Text fontSize="15px" fontWeight="semibold" color="gray.800">
              Accounts
            </Text>
            {partnerPostsEnabled ? (
              <HStack spacing={3}>
                <Text fontSize="13px" fontWeight="medium" color="gray.800">
                  Partner post
                </Text>
                <Switch
                  size="md"
                  colorScheme="blue"
                  isChecked={creativeBlockHasPartnerPost(creative)}
                  isDisabled={isDisabled}
                  onChange={(e) => {
                    if (e.target.checked) {
                      onUpdate(index, { partnerPostEnabled: true });
                      return;
                    }
                    onUpdate(index, {
                      partnerPostEnabled: false,
                      partnerInstagramUserId: undefined,
                      partnerInstagramUsername: undefined,
                      partnerFacebookPageId: undefined,
                      partnerFacebookPage: null,
                      partnerInstagramAccount: null,
                      partnerPermissionStatus: undefined,
                    });
                  }}
                />
              </HStack>
            ) : null}
          </HStack>
          <Box position="relative" zIndex={CREATIVE_IDENTITY_SELECT_CONTROL_Z + 1}>
          <HStack spacing={6} align="flex-start" flexWrap="wrap" w="full">
        <FormControl flex={1} minW="160px">
          <FormLabel fontSize="13px" fontWeight="medium" mb={2}>
            Facebook Page
          </FormLabel>
          <Select
            options={pageOptions}
            isDisabled={isDisabled || isFacebookPagesLoading}
            isLoading={isFacebookPagesLoading}
            value={
              creative.fbPage
                ? pageOptions.find(
                    (o) =>
                      o.value.id ===
                      (typeof creative.fbPage === "object"
                        ? creative.fbPage?.id
                        : creative.fbPage)
                  ) ?? null
                : null
            }
            onChange={(opt) => handleFacebookPageChange(opt?.value ?? null)}
            placeholder={
              isFacebookPagesLoading ? "Loading pages…" : "Select page"
            }
            chakraStyles={identityFacebookSelectStyles}
            formatOptionLabel={(option) => (
              <HStack spacing={2}>
                <Avatar
                  size="xs"
                  name={option.value.name}
                  src={option.value.picture}
                />
                <Text fontSize="sm" noOfLines={1}>
                  {option.value.name}
                </Text>
              </HStack>
            )}
          />
        </FormControl>
        <FormControl flex={1} minW="160px">
          <FormLabel fontSize="13px" fontWeight="medium" mb={2}>
            Instagram Account
          </FormLabel>
          <Select
            options={mergedInstagramOptions}
            isDisabled={isDisabled || isInstagramAccountsLoading}
            isLoading={isInstagramAccountsLoading}
            isClearable={!instagramAccountClearDisabled && !isInstagramAccountsLoading}
            value={
              creative.instagramAccount
                ? (mergedInstagramOptions.find(
                    (o) =>
                      o.value.id ===
                      (typeof creative.instagramAccount === "object"
                        ? creative.instagramAccount?.id
                        : creative.instagramAccount)
                  ) ?? null)
                : null
            }
            onChange={(opt) => handleInstagramChange(opt?.value ?? null)}
            placeholder={
              isInstagramAccountsLoading
                ? "Loading accounts…"
                : "Select Instagram"
            }
            chakraStyles={identityInstagramSelectStyles}
            formatOptionLabel={(option) => (
              <HStack spacing={2}>
                <Avatar
                  size="xs"
                  name={option.value.username}
                  src={option.value.profile_picture_url}
                />
                <Text fontSize="sm" noOfLines={1}>
                  {option.value.username
                    ? `@${option.value.username}`
                    : option.value.name}
                </Text>
              </HStack>
            )}
          />
        </FormControl>
      </HStack>
          </Box>

          {partnerPostEnabled && (
            <InstagramPartnerPanel
              creative={creative}
              pageOptions={pageOptions}
              instagramOptions={instagramOptions}
              partnerPages={adAccountId?.trim() ? partnerPages : undefined}
              partnerInstagramAccounts={
                adAccountId?.trim() ? partnerInstagramAccounts : undefined
              }
              partnershipOptionsLoading={
                adAccountId?.trim() ? partnershipOptionsLoading : false
              }
              partnershipPermissionsLoaded={
                adAccountId?.trim() ? partnershipPermissionsLoaded : false
              }
              partnershipOptionsError={
                adAccountId?.trim() ? partnershipOptionsError : null
              }
              adAccountId={adAccountId}
              advertiserInstagramUserId={advertiserInstagramUserId}
              onRefreshPartnershipOptions={onRefreshPartnershipOptions}
              advertiserValidationMessages={
                adAccountId?.trim() ? advertiserValidationMessages : []
              }
              isDisabled={isDisabled}
              isFacebookPagesLoading={isFacebookPagesLoading}
              isInstagramAccountsLoading={isInstagramAccountsLoading}
              onUpdate={(patch) => onUpdate(index, patch)}
              onPartnershipPermissionLookupLoadingChange={
                onPartnershipPermissionLookupLoadingChange
              }
              onPartnerFacebookPageChange={onPartnerFacebookPageChange}
              selectContainerStyles={selectContainerStyles}
            />
          )}
        </VStack>
      </Box>

      <Box mx="auto" width="full" height="0" textAlign="center" mt={4} mb={-4}>
        {allowCreativeCountChanges && index === totalCreatives - 1 && (
          <Button
            display="inline-block"
            size="sm"
            fontWeight="medium"
            variant="outline"
            rightIcon={<Text>+</Text>}
            bgColor="gray.800"
            color="white"
            width="fit-content"
            onClick={onAddMore}
          >
            Add More
          </Button>
        )}
        {allowCreativeCountChanges && totalCreatives > 1 && index !== 0 && (
          <Button
            ml={2}
            size="sm"
            variant="outline"
            bg="red.50"
            colorScheme="red"
            onClick={() => onRemove(index)}
          >
            <FaTrash />
          </Button>
        )}
      </Box>
    </Box>
  );
}
